/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {map, catchError, tap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {APP_CONFIG} from '../app.config';
import {MessageService} from "./message.service";
import {Driver} from "../model/Driver";
import {DriverTraining} from "../model/DriverTraining";
import {Observable, of} from "rxjs";
import {DriverDrivingLevel} from "../model/DriverDrivingLevel";
import {DriverEndorsement} from "../model/DriverEndorsement";
import {DriverViolation} from "../model/DriverViolation";
import {ModalComponent} from "../../assets/design-library/modal/modal.component";
import {Accident} from "../model/Accident";
import { DriverGovernmentEndorsement } from '../model/DriverGovernmentEndorsement';
import {Attachment} from "../model/attachment";

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class DriverService {
  modal= new ModalComponent();

  private driverUrl;

  private personUrl;

  constructor(private http: HttpClient,
              private messageService: MessageService) {
    this.driverUrl = inject(APP_CONFIG).baseUrl + '/api/drivers';
    this.personUrl = inject(APP_CONFIG).baseUrl + '/api/persons';

  }


  getDriver(driver: Driver | number): Observable<Driver> {
    const id = typeof driver === 'number' ? driver : driver.driverID;
    const url = `${this.driverUrl}/${id}`;

    return this.http.get<Driver>(url).pipe(
      tap(_ => this.log('got Driver "${driver}"')),
      catchError(this.handleError<Driver>('getDriver'))
    );

  }

  getDriverFromLDAP(driver: Driver | number): Observable<Driver> {
    const id = typeof driver === 'number' ? driver : driver.driverID;
    const url = `${this.driverUrl}/${id}/ldap`;

    return this.http.get<Driver>(url).pipe(
      tap(_ => this.log('got Driver "${driver}"')),
      catchError(this.handleError<Driver>('getDriverfromLDAP'))
    );

  }

  getDriverByPersonId(driver: Driver | number): Observable<Driver> {
    const id = typeof driver === 'number' ? driver : driver.personID;
    const url = `${this.driverUrl}?personId=${id}`;

    return this.http.get<Driver>(url).pipe(
      tap(_ => this.log('got Driver "${driver}"')),
      catchError(this.handleError<Driver>('getDriverByPersonId'))
    );

  }

  getDriverEndorsement(driverId): Observable<DriverEndorsement[]> {
    const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.driverUrl}/${id}/driverendorsements`;

    return this.http.get<DriverEndorsement[]>(url).pipe(
      tap(_ => this.log('got Driver Endorsement "${driver}"')),
      catchError(this.handleError<DriverEndorsement[]>('getDriverEndorsement'))
    );
  }

  getDriverGovernmentEndorsement(driverId): Observable<DriverGovernmentEndorsement[]> {
    const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.driverUrl}/${id}/drivergovernmentendorsements`;

    return this.http.get<DriverGovernmentEndorsement[]>(url).pipe(
        tap(_ => this.log('got Driver Government Endorsement "${driver}"')),
        catchError(this.handleError<DriverGovernmentEndorsement[]>('getDriverGovernmentEndorsement'))
      );
  }

  getDriverTraining(driverId): Observable<DriverTraining[]> {
    const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.driverUrl}/${id}/drivertrainings`;

    return this.http.get<DriverTraining[]>(url).pipe(
      tap(_ => this.log('got Driver Training "${driverTraining}"')),
      catchError(this.handleError<DriverTraining[]>('getDriverTraining'))
    );
  }

  getDrivingLevel(driverId): Observable<DriverDrivingLevel[]> {
  const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.driverUrl}/${id}/driverlevels`;

    return this.http.get<DriverDrivingLevel[]>(url).pipe(
      tap(_ => this.log('got Driver Level "${driver}"')),
      catchError(this.handleError<DriverDrivingLevel[]>('driverlevel'))
    );
  }

  getDriverViolation(driverId): Observable<DriverViolation[]> {
    const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.driverUrl}/${id}/violations`;

    return this.http.get<DriverViolation[]>(url).pipe(
      tap(_ => this.log('got Driver Violations for "${driver}"')),
      catchError(this.handleError<DriverViolation[]>('getDriverViolation'))
    );
  }

  getAccidentsByPersonId(personID): Observable<Accident[]> {
    const id = typeof personID === 'number' ? personID : personID;
    const url = `${this.personUrl}/${id}/accidents`;

    return this.http.get<Accident[]>(url).pipe(
      tap(_ => this.log('got Person Accident for "${driver}"')),
      catchError(this.handleError<Accident[]>('getAccidentsByPersonId'))
    );
  }

  UpdateDriverInfo(driver: Driver): Observable<Driver>{
    const id= driver.driverID;
    const url = `${this.driverUrl}/${id}`;

    return this.http.put(url,driver,httpOptions).pipe(
      map(driver => driver as Driver));
  }

  AddDriverInfo(driver: Driver): Observable<Driver>{
    const url = `${this.driverUrl}`;

    return this.http.post(url,driver,httpOptions).pipe(
      map(driver => driver as Driver));
  }

  AddDriverTraining(driverTaining: DriverTraining): Observable<DriverTraining>{
    const id= driverTaining.driverId;
    const url = `${this.driverUrl}/${id}/drivertrainings`;

    return this.http.post(url,driverTaining,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver training "${driver}"')),
        catchError(this.handleError<DriverTraining[]>('driver Training'))
      ).pipe(
      map(driverTaining => driverTaining as DriverTraining));
  }

  UpdateDriverTraining(driverTaining: DriverTraining): Observable<DriverTraining>{
    const id= driverTaining.driverId;
    const trainingId = driverTaining.driverTrainingId;
    const url = `${this.driverUrl}/${id}/drivertrainings/${trainingId}`;

    return this.http.put(url,driverTaining,httpOptions)
      .pipe(
      tap(_ => this.log('Update Driver training "${driver}"')),
      catchError(this.handleError<DriverTraining[]>('driver Training'))
    ).pipe(
      map(driverTaining => driverTaining as DriverTraining));
  }

  DeleteDriverTraining(driverTaining: DriverTraining): Observable<{}>{
    const id= driverTaining.driverId;
    const trainingId = driverTaining.driverTrainingId;
    const url = `${this.driverUrl}/${id}/drivertrainings/${trainingId}`;

    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Driving Training'))
      );
  }

  AddDrivingLevel(drivingLevel: DriverDrivingLevel): Observable<DriverDrivingLevel>{
    const id= drivingLevel.driverId;
    const url = `${this.driverUrl}/${id}/driverlevels`;

    return this.http.post(url,drivingLevel,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver Level "${driver}"')),
        catchError(this.handleError<DriverDrivingLevel[]>('driver level'))
      ).pipe(
      map(drivingLevel => drivingLevel as DriverDrivingLevel));
  }

  UpdateDrivingLevel(drivingLevel: DriverDrivingLevel): Observable<DriverDrivingLevel>{
    const id= drivingLevel.driverId;
    const levelId = drivingLevel.driverDrivingLevelId;
    const url = `${this.driverUrl}/${id}/driverlevels/${levelId}`;

    return this.http.put(url,drivingLevel,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver Level "${driver}"')),
        catchError(this.handleError<DriverDrivingLevel[]>('driver level'))
      ).pipe(
      map(drivingLevel => drivingLevel as DriverDrivingLevel));
  }

  DeleteDrivingLevel(drivingLevel: DriverDrivingLevel): Observable<{}>{
    const id= drivingLevel.driverId;
    const levelId = drivingLevel.driverDrivingLevelId;
    const url = `${this.driverUrl}/${id}/driverlevels/${levelId}`;

    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Driving Level'))
      );
  }

  AddDriverEndorsement(driverEndorsement: DriverEndorsement): Observable<DriverEndorsement>{
    const id= driverEndorsement.driverId;
    const url = `${this.driverUrl}/${id}/driverendorsements`;

    return this.http.post(url,driverEndorsement,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver endorsement "${driver}"')),
        catchError(this.handleError<DriverEndorsement[]>('driver endorsement'))
      ).pipe(
      map(driverEndorsement => driverEndorsement as DriverEndorsement));
  }

  UpdateDriverEndorsement(driverEndorsement: DriverEndorsement): Observable<DriverEndorsement>{
    const id= driverEndorsement.driverId;
    const endorsementId = driverEndorsement.driverEndorsementId;
    const url = `${this.driverUrl}/${id}/driverendorsements/${endorsementId}`;

    return this.http.put(url,driverEndorsement,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver endorsement "${driver}"')),
        catchError(this.handleError<DriverEndorsement[]>('driver endorsement'))
      ).pipe(
      map(driverEndorsement => driverEndorsement as DriverEndorsement));
  }

  DeleteDriverEndorsement(driverEndorsement: DriverEndorsement): Observable<{}>{
    const id= driverEndorsement.driverId;
    const endorsementId = driverEndorsement.driverEndorsementId;
    const url = `${this.driverUrl}/${id}/driverendorsements/${endorsementId}`;

    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Driver Endorsement'))
      );
  }


  AddDriverGovernmentEndorsement(driverGovernmentEndorsement: DriverGovernmentEndorsement): Observable<DriverGovernmentEndorsement>{
    const id= driverGovernmentEndorsement.driverId;
    const url = `${this.driverUrl}/${id}/drivergovernmentendorsements`;

    return this.http.post(url,driverGovernmentEndorsement,httpOptions)
      .pipe(
        tap(_ => this.log('Add Driver Government endorsement "${driver}"')),
        catchError(this.handleError<DriverGovernmentEndorsement[]>('driver endorsement'))
      ).pipe(
      map(driverEndorsement => driverEndorsement as DriverGovernmentEndorsement));
  }

  UpdateDriverGovernmentEndorsement(driverGovernmentEndorsement: DriverGovernmentEndorsement): Observable<DriverGovernmentEndorsement>{
    const id= driverGovernmentEndorsement.driverId;
    const endorsementId = driverGovernmentEndorsement.driverGovernmentEndorsementId;
    const url = `${this.driverUrl}/${id}/drivergovernmentendorsements/${endorsementId}`;

    return this.http.put(url,driverGovernmentEndorsement,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver endorsement "${driver}"')),
        catchError(this.handleError<DriverGovernmentEndorsement[]>('driver endorsement'))
      ).pipe(
      map(driverEndorsement => driverEndorsement as DriverGovernmentEndorsement));
  }

  DeleteDriverGovernmentEndorsement(driverGovernmentEndorsement: DriverGovernmentEndorsement): Observable<{}>{
    const id= driverGovernmentEndorsement.driverId;
    const endorsementId = driverGovernmentEndorsement.driverGovernmentEndorsementId;
    const url = `${this.driverUrl}/${id}/drivergovernmentendorsements/${endorsementId}`;

    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Driver Gov. Endorsement'))
      );
  }

  AddDriverViolation(driverViolation: DriverViolation): Observable<DriverViolation>{
    const id= driverViolation.driverId;
    const url = `${this.driverUrl}/${id}/violations`;

    return this.http.post(url,driverViolation,httpOptions)
      .pipe(
        tap(_ => this.log('Update Driver Violation "${driver}"')),
        catchError(this.handleError<DriverViolation[]>('driver violation'))
      ).pipe(
      map(driverViolation => driverViolation as DriverViolation));
  }

  UpdateDriverViolation(driverViolation: DriverViolation): Observable<DriverViolation>{
    const id= driverViolation.driverId;
    const violoationId = driverViolation.violationId;
    const url = `${this.driverUrl}/${id}/violations/${violoationId}`;

    return this.http.put(url,driverViolation,httpOptions)
      .pipe(
      tap(_ => this.log('Update Driver Violation "${driver}"')),
      catchError(this.handleError<DriverViolation[]>('driver violation'))
    ).pipe(
      map(driverViolation => driverViolation as DriverViolation));
  }

  DeleteDriverViolation(driverViolation: DriverViolation): Observable<{}>{
    const id= driverViolation.driverId;
    const violationId = driverViolation.violationId;
    const url = `${this.driverUrl}/${id}/violations/${violationId}`;

    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('Delete violation'))
      );
  }

  updateAttachmentById(id:number, fileType:string, fileId:number, attachment: Attachment): Observable<{}>{
    const url = `${this.driverUrl}/${id}/${fileType}/${fileId}/attachment`;
    return this.http.put(url,attachment,httpOptions).pipe(
      map(obj=> obj as any));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      // console.error(error); // log to console instead
      if (error.error.errorCode) {
        if (error.error.errorCode !== '404 Not Found')
          alert(error.error.errorCode + " : " + error.error.errorMessage);
      }
      else
        alert(error.status + " : " + error.error.message);


      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {
    this.messageService.add('Driver: ' + message);
  }
}

